import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import { Button } from '@/components/ui/button';

/**
 * Phase 3 Task 20. Public route — the partner's portal opens this URL with
 * a `token` query param after the user clicks the SSO button. We strip the
 * token from history immediately, send it to verify-relay-token, set the
 * resulting session, and navigate to the partner's target workspace.
 *
 * Every error_code from the verify endpoint maps to friendly user copy here.
 * If the partner is identified at error time, we offer a "Return to portal"
 * button via the partner_portal_url echoed back from the edge function.
 */

interface VerifyResponse {
  session?: { access_token: string; refresh_token: string };
  target_workspace_id?: string;
  redirect_path?: string;
  error_code?: string;
  message?: string;
  partner_portal_url?: string;
}

const ERROR_COPY: Record<string, { title: string; description: string }> = {
  invalid_token_format: {
    title: 'Invalid sign-in link',
    description: 'This sign-in link is malformed.',
  },
  unknown_partner: {
    title: 'Unknown enterprise',
    description: 'This sign-in link is not associated with a recognized enterprise.',
  },
  partner_disabled: {
    title: 'Enterprise integration paused',
    description: "Your company's BuildIt access has been suspended.",
  },
  bad_signature: {
    title: 'Invalid sign-in link',
    description: 'This sign-in link is not valid. Please try clicking the button again from your portal.',
  },
  expired: {
    title: 'Sign-in link expired',
    description: 'This sign-in link has expired. Please click the button in your portal again to get a fresh link.',
  },
  replay: {
    title: 'Sign-in link already used',
    description: 'This sign-in link has already been used. Click the button in your portal again to start a new sign-in.',
  },
  identity_revoked: {
    title: 'Access revoked',
    description: 'Your enterprise access has been removed.',
  },
  shared_workspace_deleted: {
    title: 'Workspace unavailable',
    description: 'The enterprise workspace is no longer available. Contact your enterprise administrator.',
  },
  generate_link_failed: {
    title: 'Sign-in failed',
    description: 'Could not complete sign-in. Click the button in your portal to try again.',
  },
  finalize_failed: {
    title: 'Provisioning failed',
    description: 'Sign-in completed but provisioning failed. Click the button in your portal to try again.',
  },
  session_mint_failed: {
    title: 'Session creation failed',
    description: 'Sign-in verified but session creation failed. Click the button in your portal to try again.',
  },
  rate_limited: {
    title: 'Too many attempts',
    description: 'Too many sign-in attempts. Please wait a minute and try again.',
  },
  internal_error: {
    title: 'Unexpected error',
    description: 'An unexpected error occurred.',
  },
};

interface ErrorContext {
  error_code?: string;
  partner_portal_url?: string;
}

export default function AutoLogin(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<{ code: string; portalUrl?: string } | null>(null);
  const token = params.get('token');

  useEffect(() => {
    // Strip token from URL history immediately so it doesn't survive in
    // browser back-button history or get sent on follow-up requests.
    window.history.replaceState(null, '', '/auto-login');
    // Inject a no-referrer meta tag so we don't leak the path (or any
    // pre-strip URL) on outbound link clicks while this page renders.
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!token) throw new Error('missing token');
      const { data, error: invokeErr } = await supabase.functions.invoke<VerifyResponse>(
        'verify-relay-token',
        { body: { token } },
      );
      if (invokeErr) throw invokeErr;
      return data;
    },
    onSuccess: async (data) => {
      if (data?.error_code || !data?.session || !data?.target_workspace_id) {
        setError({
          code: data?.error_code ?? 'internal_error',
          portalUrl: data?.partner_portal_url,
        });
        return;
      }
      // One-shot signal: tells useWorkspaces.pickWorkspaceOnSignIn that this
      // sign-in is via enterprise SSO and the targeted workspace must override
      // the user's is_default flag. Consumed and cleared on first read.
      sessionStorage.setItem('ssoSignInWorkspaceId', data.target_workspace_id);
      // Clear the resolved-this-session flag so useWorkspaces takes the
      // sign-in path (which honors ssoSignInWorkspaceId) rather than the
      // refresh path. Necessary if a previous session in the same tab
      // already resolved a workspace.
      sessionStorage.removeItem('workspaceResolvedSession');
      localStorage.setItem('currentWorkspaceId', data.target_workspace_id);
      const { error: setErr } = await supabase.auth.setSession(data.session);
      if (setErr) {
        captureError(setErr as Error, { feature: 'enterprise-sso-set-session' });
        setError({ code: 'internal_error' });
        return;
      }
      navigate(data.redirect_path ?? '/contacts');
    },
    onError: (err: unknown) => {
      captureError(err as Error, { feature: 'enterprise-sso-auto-login' });
      const ctx = (err as { context?: ErrorContext })?.context ?? {};
      const code = ctx.error_code ?? 'internal_error';
      const portalUrl = ctx.partner_portal_url;
      setError({ code, portalUrl });
    },
  });

  useEffect(() => {
    if (token) mutation.mutate();
    // mutate is stable; intentionally only re-run when the token changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-xl font-semibold">No sign-in token provided</h1>
        </div>
      </div>
    );
  }

  if (error) {
    const copy = ERROR_COPY[error.code] ?? ERROR_COPY.internal_error;
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-xl font-semibold">{copy.title}</h1>
          <p className="text-muted-foreground">{copy.description}</p>
          {error.portalUrl && (
            <Button asChild variant="outline">
              <a href={error.portalUrl}>Return to your portal</a>
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center space-y-2">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
        <p className="text-muted-foreground">Signing you in…</p>
      </div>
    </div>
  );
}
